import EventListenerModel from "./EventListenerModel";

/**
 * @typedef {import('../webapp/services/MemberSearchService.js').OnValidateCallback} OnValidateCallback
 */

/**
 * @typedef {import('../webapp/services/MemberSearchService.js').MemberSearchDataHostData} MemberSearchDataHostData
 */

/**
 * @typedef {import('../webapp/services/MemberSearchService.js').SearchTypeDataHostData} SearchTypeDataHostData
 */

/**
 * This class is used to manage the state of a member search component
 */
export default class MemberSearchDataHost extends EventListenerModel {
  searchTypes = [];
  selected = false;
  webAppContext = undefined;

  /**
   * @param {MemberSearchDataHostData} memberSearchData
   */
  constructor(searchTypes = []) {
    super();
    this.searchTypes = searchTypes.map((searchTypeData) => {
      // return new SearchTypeDataHost(searchTypeData, this);
      const newSearchType = new SearchTypeDataHost(searchTypeData, this);
      if (searchTypeData.selected) {
        this.selected = newSearchType;
      }
      return newSearchType;
    });

    if (!this.selected && this.searchTypes.length > 0) {
      this.searchTypes[0].setSelected(true);
    }
  }

  /**
   * This method initializes the MemberSearchDataHost with the value from the webAppContext
   * @returns {void}
   */
  init() {
    for (let i = 0; i < this.searchTypes.length; i++) {
      const searchType = this.searchTypes[i];
      searchType.init(this.webAppContext);
    }
  }

  /**
   * This method gets the data from the MemberSearchDataHost
   * @returns {Object}
   */
  getData() {
    const data = {};

    if (this.selected) {
      data.searchType = this.selected.key;

      for (let k = 0; k < this.selected.inputs.length; k++) {
        const input = this.selected.inputs[k];
        data[input.key] = input.value;
      }
    }
    return data;
  }

  /**
   * This method saves the input cache to the webAppContext
   * @param {WebAppContext} webAppContext
   */
  async saveInputCache(webAppContext) {
    const MemberLookup = webAppContext.state.MemberLookup || {};
    console.log("saveInputCache: MemberLookup (before)", MemberLookup);
    for (let i = 0; i < this.searchTypes.length; i++) {
      const searchType = this.searchTypes[i];

      if (this.searchTypes[i].selected) {
        MemberLookup.searchType = searchType.key;
      }
      console.log("saveInputCache searchType: ", searchType.key);
      for (let j = 0; j < searchType.inputs.length; j++) {
        const input = searchType.inputs[j];
        MemberLookup[input.appStateKey] = input.value;
      }
    }
    await webAppContext.setState({
      MemberLookup: MemberLookup,
    });
    console.log("saveInputCache: MemberLookup (after)", MemberLookup);
  }

  /**
   * This method validates the MemberSearchDataHost data structures
   * @returns boolean
   */
  validate() {
    let isValidated = true;
    for (let i = 0; i < this.searchTypes.length; i++) {
      const searchType = this.searchTypes[i];
      if (!searchType.selected) {
        continue;
      }

      isValidated = searchType.validate();
      // console.log(`MemberSearchDataHost: ${isValidated}`);
    }
    return isValidated;
  }

  /**
   * This method gets the SearchTypeDataHost by the key
   * @param {string} key
   * @returns {SearchTypeDataHost}
   */
  getSearchTypeBykey(key) {
    for (let i = 0; i < this.searchTypes.length; i++) {
      const searchType = this.searchTypes[i];
      if (searchType.key === key) {
        return searchType;
      }
    }
    return null;
  }

  setWebAppContext(webAppContext) {
    this.webAppContext = webAppContext;
  }
}

/**
 * This class is used to manage the state of the individual member search types in a member search component
 */
export class SearchTypeDataHost extends EventListenerModel {
  dataHost = undefined;
  label = "";
  key = "";
  selected = false;
  inputs = [];

  /**
   * @param {SearchTypeDataHostData} SearchTypeDataHostData
   */
  constructor(searchTypeData = {}, dataHost) {
    super();
    this.dataHost = dataHost;
    this.label = searchTypeData.label || "";
    this.key = searchTypeData.key || "";
    this.selected = searchTypeData.selected;
    this.inputs = (searchTypeData.inputs || []).map((input) => {
      return new SearchTypeInputDataHost(input, this);
    });
  }

  /**
   * This method initializes the SearchTypeDataHost with the value from the webAppContext
   * @param {WebAppContext} webAppContext
   * @returns {void}
   */
  init(webAppContext) {
    for (let i = 0; i < this.inputs.length; i++) {
      const input = this.inputs[i];
      input.init(webAppContext);
    }
  }

  /**
   * This method sets the selected status of the SearchTypeDataHost
   * @param {boolean} type
   */
  setSelected(type) {
    // console.log("type: ", type);
    const oldType = this.selected;
    // console.log("oldType: ", oldType);

    if (!!type === oldType) {
      return;
    }

    this.selected = !!type;
    this.processEventListener("onSelectedChanged", this.selected);

    if (!this.selected) {
      return;
    }
    this.dataHost.selected = this;
    this.dataHost.processEventListener("onSearchTypeSelected", this);
    for (let i = 0; i < this.dataHost.searchTypes.length; i++) {
      const searchType = this.dataHost.searchTypes[i];
      if (searchType === this) {
        continue;
      }
      searchType.inputs.map((input) => {
        input.error = false;
      });
      searchType.setSelected(false);
    }
  }

  /**
   * This method validates the SearchTypeDataHost data structures
   * @returns {boolean}
   */
  validate() {
    let isValidated = true;
    for (let i = 0; i < this.inputs.length; i++) {
      const input = this.inputs[i];
      const isValid = input.validate();

      if (!isValid) {
        isValidated = false;
      }
    }
    // console.log("return isValidated: ", isValidated);
    return isValidated;
  }

  /**
   * This method gets the SearchTypeInputDataHost by the key
   * @param {string} key
   * @returns {SearchTypeInputDataHost}
   */
  getInputByKey(key) {
    for (let i = 0; i < this.inputs.length; i++) {
      const input = this.inputs[i];
      if (input.key === key) {
        return input;
      }
    }
    return null;
  }
}

/**
 * This class is used to manage the state of the individual member search inputs in a member search type
 */
export class SearchTypeInputDataHost extends EventListenerModel {
  inputDataHost = undefined;
  label = "";
  key = "";
  appStateKey = "";
  type = "radio";
  placeholder = "";
  value = undefined;
  /**
   * @type {OnValidateCallback}
   */
  onValidate = undefined;
  error = false;
  validationErrorMessage = {};

  constructor(input = {}, inputDataHost) {
    super();
    this.inputDataHost = inputDataHost;
    this.label = input.label;
    this.key = input.key;
    this.type = input.type;
    this.placeholder = input.placeholder;
    this.value = input.value;
    this.error = input.error;
    this.onValidate = input.onValidate;
    this.validationErrorMessage = input.validationErrorMessage;
    this.appStateKey = input.appStateKey;
    // this.inputParams = input.inputParams || {};
  }

  /**
   * This method initializes the SearchTypeInputDataHost with the value from the webAppContext
   * @param {WebAppContext} webAppContext
   * @returns {void}
   */
  init(webAppContext) {
    console.log("webAppContext", webAppContext);
    if (!webAppContext) {
      return;
    }
    if (!this.value) {
      this.setValue(webAppContext.state.MemberLookup[this.appStateKey]);
    }
  }

  /**
   * This method sets the value of the SearchTypeInputDataHost
   * @param {string} val
   */
  setValue(val) {
    console.log("val: ", val);
    this.value = val;
    this.processEventListener("onValueChange", this.value);
  }

  /**
   * This method validates the SearchTypeInputDataHost data structures
   * @returns {boolean}
   */
  validate() {
    console.log("validate this.value: ", this.value);
    const isValid =
      typeof this.onValidate === "function"
        ? this.onValidate(this.value)
        : false;

    console.log(`SearchTypeInputDataHost ${this.label} isValid: `, isValid);

    const oldError = this.error;
    this.error = !isValid;

    this.processEventListener("onValidate", this.value);
    if (this.error) {
      // Only emit if the error status actually changed
      console.log(`oldError & this.error: ${oldError}, ${this.error}`);
      this.processEventListener("onValidateStatusChange", !isValid);
    }
    // console.log(
    //   "ServiceTypeOptionHost (" + this.label + ") isValid: ",
    //   isValid
    // );
    return isValid;
  }
}
